import { CBOS } from '../../lib/partnerNames.js';

export const PROBATION_OPTIONS = [
  { value: 'all', label: 'All Data' },
  { value: 'probation', label: 'Probation Only' },
  { value: 'nonprobation', label: 'Non Probation' },
];

export const AGENCY_OPTIONS = [
  { value: 'allsc', label: 'All Programs' },
  { value: 'ysc', label: 'Youth Service Centers' },
  { value: 'lsc', label: 'Local Service Centers' },
  { value: 'cp', label: 'Community Programs' },
];

const ALL_PREFIX = /^all/i;
export const ALL_ALLOWED_VALUE = '__ALL_ALLOWED__';
export const ALL_ALLOWED_LABEL = 'All My Agencies';

function isAggregateValue(value) {
  return typeof value === 'string' && ALL_PREFIX.test(value);
}

export function isAggregateSelection(value) {
  return isAggregateValue(value) || value === ALL_ALLOWED_VALUE;
}

export function isAllAllowedValue(value) {
  return value === ALL_ALLOWED_VALUE;
}

function buildStaticCboOptions(agencyType = 'allsc') {
  const source = CBOS[agencyType] || CBOS.allsc;
  return Object.entries(source).map(([value, label]) => ({
    value,
    label,
    isAggregate: isAggregateValue(value),
    isSynthetic: false,
  }));
}

function resolveAllowedPartners(agencyType, allowedPartners = []) {
  const normalizedType = agencyType === 'allsc' ? null : agencyType;
  return allowedPartners.filter((partner) => {
    if (!partner || !partner.partner_name) {
      return false;
    }
    if (!normalizedType) {
      return true;
    }
    return partner.partner_type === normalizedType;
  });
}

export function buildCboOptions(
  agencyType = 'allsc',
  { allowAllAgencies = true, allowedPartners = [] } = {}
) {
  if (allowAllAgencies || !Array.isArray(allowedPartners) || allowedPartners.length === 0) {
    return buildStaticCboOptions(agencyType);
  }

  const filtered = resolveAllowedPartners(agencyType, allowedPartners);
  if (filtered.length === 0) {
    return [];
  }

  const allLabels = CBOS.allsc || {};
  const options = filtered.map((partner) => ({
    value: partner.partner_name,
    label: allLabels[partner.partner_name] || partner.partner_name,
    isAggregate: false,
    isSynthetic: false,
  }));

  if (!allowAllAgencies && options.length > 1) {
    options.unshift({
      value: ALL_ALLOWED_VALUE,
      label: ALL_ALLOWED_LABEL,
      isAggregate: true,
      isSynthetic: true,
    });
  }

  return options;
}

export function deriveDefaultCbos(agencyType = 'allsc', options) {
  const builtOptions = buildCboOptions(agencyType, options);
  if (!builtOptions.length) {
    return [];
  }
  const allAllowedOption = builtOptions.find((option) => option.value === ALL_ALLOWED_VALUE);
  if (allAllowedOption) {
    return [ALL_ALLOWED_VALUE];
  }
  return [builtOptions[0].value];
}

export function sanitizeCboSelection(selection = [], agencyType = 'allsc', options) {
  const builtOptions = buildCboOptions(agencyType, options);
  const optionValues = new Set(builtOptions.map((option) => option.value));
  const normalized = Array.isArray(selection)
    ? selection
    : selection == null
      ? []
      : [selection];
  const filtered = normalized
    .map((value) => (value && value.value ? value.value : value))
    .filter((value) => optionValues.has(value));

  if (filtered.length === 0 || builtOptions.length === 0) {
    return deriveDefaultCbos(agencyType, options);
  }

  if (filtered.some((value) => value === ALL_ALLOWED_VALUE)) {
    return [ALL_ALLOWED_VALUE];
  }

  if (filtered.some((value) => isAggregateValue(value))) {
    return deriveDefaultCbos(agencyType, options);
  }

  const deduped = [];
  const seen = new Set();
  filtered.forEach((value) => {
    if (seen.has(value)) {
      return;
    }
    seen.add(value);
    deduped.push(value);
  });

  return deduped;
}

export function getProbationOption(value) {
  return PROBATION_OPTIONS.find((option) => option.value === value) || PROBATION_OPTIONS[0];
}

export function getAgencyOption(value, options = AGENCY_OPTIONS) {
  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }
  return options.find((option) => option.value === value) || options[0];
}
